<template>
	<div style="display: flex; align-items: center; justify-content: center">
		<OTPBox
			v-for="(item, i) in numInputs"
			:key="i"
			:focus="activeInput === i"
			:value="otp[i]"
			:separator="separator"
			:input-type="showPassword ? 'text' : 'password' "
			:input-classes="['otp-boxes', inputClasses]"
			:input-mode="inputMode"
			:is-last-child="i === numInputs - 1"
			:shouldAutoFocus="shouldAutoFocus"
			@on-change="handleOnChange"
			@on-keydown="handleOnKeyDown"
			@on-paste="handleOnPaste"
			@on-focus="handleOnFocus(i)"
			@on-blur="handleOnBlur"
		/>
		<button @click="showPassword = !showPassword">
			<i :class="showPassword ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
		</button>
	</div>
</template>

<script>
import OTPBox from '@/components/otp/box'
// keyCode constants
const BACKSPACE = 8
const LEFT_ARROW = 37
const RIGHT_ARROW = 39
const DELETE = 46

export default {
	components: { OTPBox },
	data() {
		return {
			activeInput: 0,
			otp: [],
			oldOtp: [],
			showPassword: false,
		}
	},

	props: {
		numInputs: {
			default: 4,
		},

		separator: {
			type: String,
			default: '',
		},

		inputClasses: {
			type: String,
		},

		inputType: {
			type: String,
			validator(value) {
				return ['number', 'tel', 'password'].includes(value)
			},
		},

		inputMode: {
			type: String,
			validator(value) {
				return ['text', 'numeric', 'tel', 'none'].includes(value)
			},
		},

		shouldAutoFocus: {
			type: Boolean,
			default: false,
		},

		hasDefaultValue: {
			type: Boolean,
			default: false,
		},

		canPaste: {
			type: Boolean,
			default: true,
		},

		defaultValue: {
			type: String,
		},
	},

	methods: {
		handleDefaultValue(value) {
			this.clearInput()
			const pastedData = value
				.slice(0, this.numInputs - this.activeInput)
				.split('')

			if (
				this.inputType === 'number' &&
				!pastedData.join('').match(/^\d+$/)
			) {
				return 'Invalid pasted data'
			}
			// Paste data from focused input onwards
			const currentCharsInOtp = this.otp.slice(0, this.activeInput)
			const combinedWithPastedData = currentCharsInOtp.concat(pastedData)
			this.$set(
				this,
				'otp',
				combinedWithPastedData.slice(0, this.numInputs)
			)
			this.focusInput(
				combinedWithPastedData.slice(0, this.numInputs).length
			)
			return this.checkFilledAllInputs()
		},

		handleOnFocus(index) {
			this.activeInput = index
			if (this.inputType == 'password') {
				this.computedInputType = 'tel'

				setTimeout(() => {
					this.computedInputType = 'password'
				}, 400)
			}
		},

		handleOnBlur() {
			this.activeInput = -1
		},

		// Helper to return OTP from input
		checkFilledAllInputs() {
			if (this.otp.join('').length === this.numInputs) {
				return this.$emit('on-complete', this.otp.join(''))
			}
			return 'Wait until the user enters the required number of characters'
		},

		// Focus on input by index
		focusInput(input) {
			this.activeInput = Math.max(Math.min(this.numInputs - 1, input), 0)
		},

		// Focus on next input
		focusNextInput() {
			this.focusInput(this.activeInput + 1)
		},

		// Focus on previous input
		focusPrevInput() {
			this.focusInput(this.activeInput - 1)
		},

		// Change OTP value at focused input
		changeCodeAtFocus(value) {
			this.oldOtp = Object.assign([], this.otp)
			this.$set(this.otp, this.activeInput, value)
			if (this.oldOtp.join('') !== this.otp.join('')) {
				this.$emit('on-change', this.otp.join(''))
				this.checkFilledAllInputs()
			}
		},

		// Handle pasted OTP
		handleOnPaste(event) {
			if (this.canPaste) {
				event.preventDefault()
				const pastedData = event.clipboardData
					.getData('text/plain')
					.slice(0, this.numInputs - this.activeInput)
					.split('')
				if (
					this.inputType === 'number' &&
					!pastedData.join('').match(/^\d+$/)
				) {
					return 'Invalid pasted data'
				}
				// Paste data from focused input onwards
				const currentCharsInOtp = this.otp.slice(0, this.activeInput)
				const combinedWithPastedData =
					currentCharsInOtp.concat(pastedData)
				this.$set(
					this,
					'otp',
					combinedWithPastedData.slice(0, this.numInputs)
				)
				this.focusInput(
					combinedWithPastedData.slice(0, this.numInputs).length
				)
				return this.checkFilledAllInputs()
			} else {
				event.preventDefault()
				return
			}
		},

		handleOnChange(value) {
			this.changeCodeAtFocus(value)
			this.focusNextInput()
		},

		clearInput() {
			if (this.otp.length > 0) {
				this.$emit('on-change', '')
			}
			this.otp = []
			this.activeInput = 0
		},

		// Handle cases of backspace, delete, left arrow, right arrow
		handleOnKeyDown(event) {
			switch (event.keyCode) {
				case BACKSPACE:
					event.preventDefault()
					this.changeCodeAtFocus('')
					this.focusPrevInput()
					break
				case DELETE:
					event.preventDefault()
					this.changeCodeAtFocus('')
					break
				case LEFT_ARROW:
					event.preventDefault()
					this.focusPrevInput()
					break
				case RIGHT_ARROW:
					event.preventDefault()
					this.focusNextInput()
					break
				default:
					break
			}
		},
	},

	created() {
		this.$on('updateValue', (value) => {
			this.handleDefaultValue(value)
		})
	},

	mounted() {
		if (this.hasDefaultValue) {
			this.handleDefaultValue(this.defaultValue)
		}
	},
}
</script>

<style>
.otp-boxes {
  height: 42px !important;
  width: 42px !important;
  padding: 5px;
  margin-right: 12px;
  font-size: 20px;
  font-weight: 500;
  border-radius: 8.5px;
  color: #1e2229;
  border: 1px solid #e9e9eb;
  text-align: center;
}

.otp-boxes:focus-visible {
  border: 1px solid #26c165;
  outline: none;
}

.otp-boxes::-webkit-inner-spin-button,
.otp-boxes::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>